import { DocContent } from '../docsData';

const doc: DocContent = {
  title: 'Column Formatting',
  content: `
Pliable makes it easy to clean data coming in from your sources. You can set up cleaning rules in two places:

1. In the **Data Cleaning Wizard**
2. On the **Node Configuration** screen, under "Configure Columns"

# Naming and Describing Columns

- Give your columns names that make sense in your business, rather than whatever they are named in your data source.
- Describe columns so that your coworkers understand what's in them.

# Data Types

Column data types in Pliable are important — they ensure that your data is in the correct format, and allow it to be used properly downstream to calculate metrics.

When you assign a data type to a column, Pliable will attempt to convert all data in that column to the designated data type. Data that is not able to be converted to that data type will come through as NULL.

> 💡 Tip: You may want to pair a data type (for example, INTEGER or DECIMAL) with a transformer (like "Remove Non-Numeric Characters")

# Available Data Types

| Data Type | Description |
|-----------|-------------|
| TEXT | Free-form text values |
| INTEGER | Whole numbers |
| DECIMAL | Numbers with decimal places |
| DATE | Date values |
| DATETIME | Date and time values |
| BOOLEAN | True/false values |

# Transforms

## Simple Transformations

Pliable has a plethora of basic transformations built-in for common cleaning use cases. When dealing with free text data, such as phone numbers or zip codes, uniformity is often an issue. Data can come in various formats, making it difficult to analyze and visualize. Pliable provides standardizing functions to ensure that these types of data are consistent and uniform, making them easier to work with.

## Translate

If you select **TRANSLATE** as a transformation for a column, you will be presented with a table that you can use to manually normalize columns.

You can translate exact matches simply by putting the value you want to translate under **Value**, and the translation under **Translation**. You can also translate using wild cards to automatically normalize anything that matches a certain pattern. For example, you might clean up a "Title" or "Role" column coming in from your CRM, so that anyone with "Vice" in their title (e.g. "Vice President of Marketing") gets converted to "Vice President".

## Custom Transform

You can also write a completely custom column transformation if the pre-set options aren't enough. The custom transform gets executed as part of the DBT build process and thus needs to be written in Snowflake-compatible SQL. Anywhere you need to reference the source value, you can just write \`[VALUE]\` and Pliable will automatically swap that out with the upstream column name. See Snowflake's documentation on Functions and Stored Procedures for a complete list.

For example, if you want to write an expression that splits up a URL path to get the first segment:

\`\`\`sql
UPPER(SPLIT_PART(LTRIM([VALUE], '/'), '/', 1))
\`\`\`
`,
};

export default doc;
